import { Nullable } from "../..";
import Guard from "../../guard";
import { EntityCopyOptions, IEntityCopyOptions } from "./entity-copy-options";
import { PropertyCopyMethod } from "./property-copy-method";
import { IPropertyCopyOption, PropertyCopyOption } from "./property-copy-option";
import { RelationCopyMethod } from "./relation-copy-method";
import { IRelationCopyOption, RelationCopyOption } from "./relation-copy-option";

/**
 * A builder for instances of <see cref="IEntityCopyOptions"/>.
 */
export interface IEntityCopyOptionsBuilder {
    /**
     * Indicates that the entity should be copied with the specified property.
     *
     * @param property - The name of the property.
     * @param method - The copy method.
     * @param newValue - The new value for the property.
     *
     * @returns The copy options builder.
     */
    withProperty(property: string, method: PropertyCopyMethod, newValue: Nullable<string>): IEntityCopyOptionsBuilder;

    /**
     * Indicates that the entity should be copied with the specified properties.
     *
     * @param properties - The properties collection.
     *
     * @returns The copy options builder.
     */
    withProperties(properties: Array<IPropertyCopyOption>): IEntityCopyOptionsBuilder;

    /** Indicates that the entity should be copied with the specified relation.
     *
     * @param relation - The name of the relation.
     * @param method - The copy method.
     * @param relatedOptions - The related options.
     *
     * @returns The copy options builder.
     */
    withRelation(
        relation: string,
        method: RelationCopyMethod,
        relatedOptions: Nullable<IEntityCopyOptions>
    ): IEntityCopyOptionsBuilder;

    /**
     * Indicates that the entity should be copied with the specified relations.
     *
     * @param relations - The relations collection.
     *
     * @returns The copy options builder.
     */
    withRelations(relations: Array<IRelationCopyOption>): IEntityCopyOptionsBuilder;

    /**
     * Indicates that the entity should be copied to the entity with the specified id.
     *
     * @param id - The destination entity identifier.
     *
     * @returns The copy options builder.
     */
    withDestinationEntityId(id: number): IEntityCopyOptionsBuilder;

    /**
     * Indicates that the entity should be copied using the copy profile with the specified id.
     *
     * @param id - The id of the copy profile.
     *
     * @returns The copy options builder.
     */
    withCopyProfileId(id: number): IEntityCopyOptionsBuilder;

    /**
     * Indicates that the entity should be copied using the copy profile with the specified identifier.
     *
     * @param identifier - The identifier of the copy profile.
     *
     * @returns The copy options builder.
     */
    withCopyProfileIdentifier(identifier: string): IEntityCopyOptionsBuilder;

    /**
     * Gets the resulting instance of IEntityCopyOptions.
     *
     * @returns The copy options.
     */
    build(): IEntityCopyOptions;
}

export class EntityCopyOptionsBuilder implements IEntityCopyOptionsBuilder {
    private readonly _copyOptions: IEntityCopyOptions = new EntityCopyOptions();

    constructor(copyOptions?: IEntityCopyOptions) {
        if (copyOptions) {
            this._copyOptions = new EntityCopyOptions();
            this._copyOptions.relationCopyOptions = copyOptions.relationCopyOptions;
            this._copyOptions.propertyCopyOptions = copyOptions.propertyCopyOptions;
            this._copyOptions.destinationEntityId = copyOptions.destinationEntityId;
            this._copyOptions.copyProfileId = copyOptions.copyProfileId;
            this._copyOptions.copyProfileIdentifier = copyOptions.copyProfileIdentifier;
        }
    }

    /**
     * {@inheritDoc}
     */
    withProperty(
        property: string,
        method: PropertyCopyMethod,
        newValue: Nullable<string> = null
    ): IEntityCopyOptionsBuilder {
        Guard.stringNotNullOrEmpty(property);
        Guard.notNullOrUndefined(method);

        return this.withProperties([new PropertyCopyOption(property, method, newValue)]);
    }

    /**
     * {@inheritDoc}
     */
    withProperties(properties: Array<IPropertyCopyOption>): IEntityCopyOptionsBuilder {
        Guard.notNullOrUndefined(properties);

        this._copyOptions.propertyCopyOptions = [
            ...new Set((this._copyOptions.propertyCopyOptions || []).concat(properties)),
        ];

        return this;
    }

    /**
     * {@inheritDoc}
     */
    withRelation(
        relation: string,
        method: RelationCopyMethod,
        relatedOptions: Nullable<IEntityCopyOptions> = null
    ): IEntityCopyOptionsBuilder {
        Guard.stringNotNullOrEmpty(relation);
        Guard.notNullOrUndefined(method);

        return this.withRelations([new RelationCopyOption(relation, method, relatedOptions)]);
    }

    /**
     * {@inheritDoc}
     */
    withRelations(relations: Array<IRelationCopyOption>): IEntityCopyOptionsBuilder {
        Guard.notNullOrUndefined(relations);

        this._copyOptions.relationCopyOptions = [
            ...new Set((this._copyOptions.relationCopyOptions || []).concat(relations)),
        ];

        return this;
    }

    /**
     * {@inheritDoc}
     */
    withDestinationEntityId(id: number): IEntityCopyOptionsBuilder {
        Guard.validId(id);

        this._copyOptions.destinationEntityId = id;

        return this;
    }

    /**
     * {@inheritDoc}
     */
    withCopyProfileId(id: number): IEntityCopyOptionsBuilder {
        Guard.validId(id);

        this._copyOptions.copyProfileId = id;
        return this;
    }

    /**
     * {@inheritDoc}
     */
    withCopyProfileIdentifier(identifier: string): IEntityCopyOptionsBuilder {
        Guard.stringNotNullOrEmpty(identifier);

        this._copyOptions.copyProfileIdentifier = identifier;

        return this;
    }

    /**
     * {@inheritDoc}
     */
    build(): IEntityCopyOptions {
        return this._copyOptions;
    }
}
